import type { ElementType, ReactNode, CSSProperties } from 'react';
import { cn } from './cn';

/**
 * Minimal Ant Design `Typography` replacement (Title / Text / Paragraph) so we can
 * drop `import { Typography } from 'antd'`. Covers the props actually used in-app.
 */
/* Levels now resolve to the two semantic heading ROLES from the type scale (theme.css) rather than
   five hand-picked size/weight pairs. In practice the app only ever uses 3/4/5 — 54 × level 4 (the
   page-title role), 14 × level 5 (section header), 1 × level 3 — so this collapses the ladder onto
   `type-page-title` and `type-section-title`, which is the point: one spelling per role, shared with
   PageHeader/SectionHeader/Card/Modal/Drawer so a Typography.Title heading and a PageHeader heading
   can no longer disagree. Levels 1–2 are unused but kept mapped for the semantic <h1>/<h2> tags. */
const LEVEL: Record<number, string> = {
  1: 'type-page-title',
  2: 'type-page-title',
  3: 'type-section-title',
  4: 'type-page-title',
  5: 'type-section-title',
};

function Title({ level = 1, children, className, style }: { level?: 1 | 2 | 3 | 4 | 5; children?: ReactNode; className?: string; style?: CSSProperties }) {
  const Tag = (`h${level}` as ElementType);
  return <Tag className={cn(LEVEL[level], 'text-content', className)} style={style}>{children}</Tag>;
}

const TEXT_TYPE: Record<string, string> = {
  secondary: 'text-muted',
  success: 'text-brand-600 dark:text-brand-400',
  warning: 'text-warning-600 dark:text-harvest-300',
  danger: 'text-danger-600 dark:text-danger-500',
};

function Text({ type, strong, italic, underline, delete: del, disabled, code, children, className, style, ...rest }: {
  type?: 'secondary' | 'success' | 'warning' | 'danger'; strong?: boolean; italic?: boolean; underline?: boolean;
  delete?: boolean; disabled?: boolean; code?: boolean; children?: ReactNode; className?: string; style?: CSSProperties;
} & Record<string, unknown>) {
  // antd's `code` had no implementation here, so it fell through `...rest` onto the <span> as a DOM
  // attribute: React warned on every render AND the identifier rendered as plain body text. Same
  // monospace chip the detail pages already use for GSTIN/PAN so identifiers read alike app-wide.
  return (
    <span
      className={cn(
        type ? TEXT_TYPE[type] : 'text-content',
        strong && 'type-strong', italic && 'italic', underline && 'underline',
        del && 'line-through', disabled && 'text-subtle',
        code && 'rounded-md border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[0.9em] tracking-tight',
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </span>
  );
}

function Paragraph({ children, className, style, type }: { children?: ReactNode; className?: string; style?: CSSProperties; type?: 'secondary' | 'success' | 'warning' | 'danger' }) {
  return <p className={cn('mb-3 leading-relaxed', type ? TEXT_TYPE[type] : 'text-content', className)} style={style}>{children}</p>;
}

function Link({ children, className, ...rest }: { children?: ReactNode; className?: string } & Record<string, unknown>) {
  return <a className={cn('text-brand-700 hover:text-brand-800 dark:text-brand-400', className)} {...rest}>{children}</a>;
}

export const Typography = Object.assign(
  function TypographyRoot({ children, className }: { children?: ReactNode; className?: string }) {
    return <div className={className}>{children}</div>;
  },
  { Title, Text, Paragraph, Link },
);
